import { ChangeEvent, useRef } from 'react';
import classNames from 'classnames';

import style from './SupportWidget.module.scss';

import { SupportWidgetTestAttributes } from 'chaika-test-attributes';
import type { TranslationValues } from 'chaika-translation-data';

import { translate } from '@chaika/i18n/helper';
import AttachedIcon from '@components/Icon/svg/attached.svg';

import { EmojiPicker } from './EmojiPicker';
import { ComposerAttachment, SupportAttachmentError } from './hooks/useComposerAttachments';
import { MAX_MESSAGE_LENGTH, SupportComposer as SupportComposerState } from './hooks/useSupportComposer';
import { SupportAttachmentFileName } from './SupportAttachmentPreview';

const NUMBER_FORMAT = new Intl.NumberFormat('ru-RU');
const FILE_SIZE_UNITS: TranslationValues[] = [
    'supportFileSizeBytes',
    'supportFileSizeKilobytes',
    'supportFileSizeMegabytes',
];

interface Props {
    composer: SupportComposerState;
    onFocus(): void;
    onBlur(): void;
}

export function SupportComposer({ composer, onFocus, onBlur }: Props) {
    const attachmentInputRef = useRef<HTMLInputElement>(null);

    const handleAttachmentInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        composer.attachFiles(Array.from(event.target.files ?? []));
        event.target.value = '';
    };

    return (
        <div className={style.composer}>
            {composer.attachments.length > 0 && (
                <ul className={style.attachmentChips}>
                    {composer.attachments.map(attachment => (
                        <AttachmentChip
                            key={attachment.localId}
                            attachment={attachment}
                            onRemove={() => composer.removeAttachment(attachment.localId)}
                            onRetry={() => composer.retryAttachment(attachment.localId)}
                        />
                    ))}
                </ul>
            )}
            {composer.isLimitExceeded && (
                <div className={style.composerError}>{translate('supportAttachmentLimit')}</div>
            )}
            <textarea
                ref={composer.textareaRef}
                className={style.composerInput}
                value={composer.text}
                rows={2}
                placeholder={translate('supportMessagePlaceholder')}
                aria-label={translate('supportMessagePlaceholder')}
                disabled={composer.isDisabled}
                data-test={SupportWidgetTestAttributes.MessageInput}
                data-testid={SupportWidgetTestAttributes.MessageInput}
                onChange={event => composer.changeText(event.target.value)}
                onKeyDown={composer.handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
            />
            <div className={style.composerToolbar}>
                {!composer.isDisabled && (
                    <>
                        <button
                            className={style.composerIconButton}
                            type={'button'}
                            aria-label={translate('supportAttachFile')}
                            title={translate('supportAttachFile')}
                            onClick={() => attachmentInputRef.current?.click()}
                        >
                            <AttachedIcon aria-hidden={'true'} />
                        </button>
                        <EmojiPicker
                            isDisabled={composer.isDisabled}
                            onSelect={composer.insertEmoji}
                        />
                    </>
                )}
                {composer.isCounterVisible && (
                    <span
                        className={classNames(
                            style.messageLengthCounter,
                            composer.isTextTooLong && style.composerError,
                        )}
                        data-test={SupportWidgetTestAttributes.MessageLengthCounter}
                        data-testid={SupportWidgetTestAttributes.MessageLengthCounter}
                    >
                        {`${NUMBER_FORMAT.format(composer.text.length)} / ${NUMBER_FORMAT.format(MAX_MESSAGE_LENGTH)}`}
                    </span>
                )}
                <button
                    className={style.sendButton}
                    type={'button'}
                    disabled={!composer.canSend}
                    aria-label={translate('supportSend')}
                    title={translate('supportSend')}
                    onClick={() => void composer.send()}
                >
                    {translate('supportSend')}
                </button>
            </div>
            <input
                ref={attachmentInputRef}
                className={style.attachmentInput}
                type={'file'}
                multiple
                disabled={composer.isDisabled}
                aria-label={translate('supportAttachFile')}
                data-test={SupportWidgetTestAttributes.AttachmentInput}
                data-testid={SupportWidgetTestAttributes.AttachmentInput}
                onChange={handleAttachmentInputChange}
            />
        </div>
    );
}

interface AttachmentChipProps {
    attachment: ComposerAttachment;
    onRemove(): void;
    onRetry(): void;
}

function AttachmentChip({ attachment, onRemove, onRetry }: AttachmentChipProps) {
    const { file, state } = attachment;

    return (
        <li
            className={classNames(style.attachmentChip, state.kind === 'failed' && style.attachmentChipFailed)}
            data-test={SupportWidgetTestAttributes.AttachmentChip}
            data-testid={SupportWidgetTestAttributes.AttachmentChip}
        >
            <SupportAttachmentFileName
                className={style.attachmentChipName}
                fileName={file.name}
            />
            <span className={style.attachmentChipMeta}>{formatFileSize(file.size)}</span>
            {state.kind === 'uploading' && (
                <progress
                    className={style.attachmentChipProgress}
                    value={state.progress}
                    max={1}
                />
            )}
            {state.kind === 'failed' && (
                <span className={style.attachmentError}>
                    {translate(getAttachmentErrorTranslationKey(state.error))}
                </span>
            )}
            <span className={style.attachmentChipActions}>
                {state.kind === 'failed' && state.error !== 'tooLarge' && (
                    <button
                        className={style.textButton}
                        type={'button'}
                        onClick={onRetry}
                    >
                        {translate('supportAttachmentRetry')}
                    </button>
                )}
                <button
                    className={style.textButton}
                    type={'button'}
                    aria-label={translate('supportRemoveAttachment', { $name: file.name })}
                    onClick={onRemove}
                >
                    {translate('supportAttachmentRemove')}
                </button>
            </span>
        </li>
    );
}

function formatFileSize(size: number): string {
    const unitIndex = Math.min(Math.floor(Math.log(Math.max(size, 1)) / Math.log(1024)), FILE_SIZE_UNITS.length - 1);
    const value = Number((size / 1024 ** unitIndex).toFixed(unitIndex === 0 ? 0 : 1));

    return translate(FILE_SIZE_UNITS[unitIndex], { $size: NUMBER_FORMAT.format(value) });
}

function getAttachmentErrorTranslationKey(error: SupportAttachmentError): TranslationValues {
    switch (error) {
        case 'invalid':
            return 'supportAttachmentInvalid';
        case 'tooLarge':
            return 'supportAttachmentTooLarge';
        case 'uploadFailed':
            return 'supportAttachmentUploadFailed';
    }
}
